import React, { useCallback, useEffect, useRef, useState } from "react";
import { Wrench, Package, Truck, Building2, CircleDot } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { money, num } from "@/lib/sk";

const ICON = { arbeid: Wrench, materiell: Package, utstyr: Truck, underleverandor: Building2, annet: CircleDot };
export const K = (v) => `${money(v, "NOK", 1, { bare: true })} kr`;
const n = (v) => (v == null ? "" : num(v, Number.isInteger(v) ? 0 : 1));
const FRESH_MS = 24 * 3600 * 1000;

export const isFresh = (p) => Boolean(p && !p.stale && Date.now() - new Date(p.created_at).getTime() < FRESH_MS);
export const isReady = (p) => (p.level === "sikkert" || p.level === "forslag") && !p.applied && !p.manual;
export const isMissing = (p) => p.level === "mangler" && !p.applied;
/** What the plan offers right now: posts that can go in, posts that need an answer first. */
export const planCounts = (plan) => { const items = plan?.items || []; return { ready: items.filter(isReady).length, missing: items.filter(isMissing).length, applied: items.filter((p) => p.applied).length }; };

/**
 * The assistant's calculation proposals for a case. One full plan (all open posts) and, separately, the latest per-post
 * reading – so a quick «foreslå for posten» never hides the overview. Sums come from the calc engine; apply is the approval.
 */
export function useForslag(caseId) {
  const [plan, setPlan] = useState(null);
  const [scoped, setScoped] = useState(null);
  const [state, setState] = useState("idle");
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const take = useCallback((p) => { if (!p) return; if (p.scope) setScoped(p); else setPlan(p); }, []);
  useEffect(() => {
    setPlan(null); setScoped(null);
    api.get(`/projects/${caseId}/kalkyle/klargjor`).then((r) => { if (alive.current) take(r.data.plan); }).catch(() => {});
  }, [caseId, take]);

  const analyse = useCallback(async (itemIds) => {
    setState("running");
    try {
      const r = await api.post(`/projects/${caseId}/kalkyle/klargjor`, { item_ids: itemIds?.length ? itemIds : null });
      if (alive.current) { take(r.data.plan); setState("done"); }
      return r.data.plan;
    } catch (e) {
      toast.error(errMsg(e));
      if (alive.current) setState("error");
      return null;
    }
  }, [caseId, take]);

  const apply = useCallback(async (p, ids, mode, ops) => {
    const r = await api.post(`/projects/${caseId}/kalkyle/klargjor/${p.id}/apply`, { item_ids: ids, mode });
    ops.apply({ project: r.data.project, context: r.data.context });
    take(r.data.plan);
    return r.data;
  }, [caseId, take]);

  const undo = useCallback((changeId, ops) => ops.undo(changeId).then(() => api.get(`/projects/${caseId}/kalkyle/klargjor`).then((r) => take(r.data.plan))).catch(() => {}), [caseId, take]);
  const forItem = useCallback((id) => { const pick = (p) => (p?.items || []).find((x) => x.item_id === id); return (isFresh(scoped) && pick(scoped)) || (isFresh(plan) && pick(plan)) || null; }, [plan, scoped]);
  return { plan, scoped, state, analyse, apply, undo, forItem };
}

/** The proposed lines of one post, as they will read in the calculation: what · «12 t × 875 kr». */
export function ProposalLines({ lines, className }) {
  if (!lines?.length) return null;
  return (
    <ul className={cn("space-y-1.5", className)} data-testid="forslag-lines">
      {lines.map((ln, i) => {
        const Icon = ICON[ln.kind] || CircleDot;
        const qty = ln.qty == null ? "mengde må settes" : `${n(ln.qty)} ${ln.unit || ""}`.trim();
        const price = ln.unit_price != null ? `× ${money(ln.unit_price, "NOK", 1, { bare: true })} kr` : ln.unit_cost != null ? `× ${money(ln.unit_cost, "NOK", 1, { bare: true })} kr kost` : "· venter på pris";
        return (
          <li key={i} className="flex items-baseline gap-3 text-[14.5px] leading-[1.5]" data-testid="forslag-line" data-open={ln.open ? "1" : "0"} data-awaiting={ln.awaiting_price ? "1" : "0"}>
            <Icon className="h-3.5 w-3.5 shrink-0 translate-y-[2px] text-foreground/45" strokeWidth={1.9} />
            <span className="min-w-0 flex-1 text-foreground/90">{ln.description}</span>
            <span className={cn("tabular shrink-0 text-[13.5px]", ln.open || ln.awaiting_price ? "text-[hsl(var(--v-assumed))]" : "text-muted-foreground")}>{qty} {price}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** What the proposal adds up to: Σ qty × sats over the priced lines (never 0 as a stand-in); null when nothing is priced yet. */
export function proposalSum(p) {
  const lines = p.lines || [];
  const priced = lines.filter((l) => l.total_price != null);
  if (priced.length) return priced.reduce((n, l) => n + l.total_price, 0);
  return p.effect?.price || null;
}
/** Lines that still wait on something: a quantity («open») or a price to be obtained («awaiting_price»). */
export const openLines = (p) => (p.lines || []).filter((l) => l.open || l.awaiting_price);
const FORM = { etter_regning: "etter regning – utenfor summen", enhetspris: "enhetspris – mengden avregnes", opsjon: "opsjon – utenfor summen" };
/** «Foreslått salg 17 500 kr» – or what stands in its way. */
export function ProposalSum({ p, testId }) {
  const sum = proposalSum(p);
  const form = FORM[p.price_type];
  const open = openLines(p);
  return (
    <div className="flex items-baseline justify-between gap-3 text-[14.5px]" data-testid={testId} data-partial={open.length ? "1" : "0"}>
      <span className="text-muted-foreground">Foreslått salg{form ? <span className="ml-1.5 text-[13px]">· {form}</span> : null}</span>
      <span className={cn("tabular text-[15px]", sum != null ? "font-semibold" : "text-muted-foreground")}>
        {sum != null ? K(sum) : "avhenger av mengde"}
        {sum != null && open.length ? <span className="ml-1.5 text-[13px] font-normal text-[hsl(var(--v-assumed))]">+ {open.length} {open.length === 1 ? "linje" : "linjer"} åpen</span> : null}
      </span>
    </div>
  );
}

/** The honest footnote under a proposal: what is grounded, what is an estimate Egil must confirm, what still has to be obtained. */
export function Assumption({ p, className }) {
  if (p.level === "sikkert") return <p className={cn("text-[13.5px] leading-[1.5] text-muted-foreground", className)} data-testid={`forslag-basis-${p.ref}`}>Mengder fra kundens tekst, gjeldende satser.</p>;
  if (p.level !== "forslag") return null;
  const waits = (p.lines || []).filter((l) => l.awaiting_price);
  const unsized = (p.lines || []).filter((l) => l.open);
  return (
    <p className={cn("text-[13.5px] leading-[1.55] text-muted-foreground", className)} data-testid={`forslag-basis-${p.ref}`}>
      <span className="font-medium text-foreground/75">Antakelse</span> · Priset med gjeldende satser; timene er et faglig anslag og må bekreftes.{p.why ? ` ${p.why}` : ""}
      {waits.length || unsized.length ? (
        <span className="mt-1 block" data-testid={`forslag-open-${p.ref}`}>
          {waits.length ? <>{waits.map((l) => l.description).join(", ")} legges inn uten beløp – pris innhentes{p.question_to === "leverandor" ? " fra leverandør" : ""}. </> : null}
          {unsized.length ? <>{unsized.map((l) => l.description).join(", ")} legges inn uten mengde. </> : null}
          Summen er foreløpig til det er på plass.
        </span>
      ) : null}
    </p>
  );
}
